import {
  GeneratedPackageDataType,
  PackageType,
  PdfOrSecurityPackageDataType,
} from 'typings/wallet.type';
import i18n from 'locales/fa.json';
import { BasketType } from 'typings/profile.type';
import { addTooman } from './common.util';
import { EnumPermission } from './permission.util';

export const generatePackagesData = (
  packageData: PackageType[],
): GeneratedPackageDataType => {
  const newPackagesData = {};
  packageData?.forEach((item) => {
    if (!newPackagesData[item.package_type]) {
      newPackagesData[item.package_type] = [];
    }
    newPackagesData[item.package_type].push(item);
  });
  return newPackagesData;
};

export const handlePackageQueryParams = (
  selectedPackage: PackageType,
  packageList?: PdfOrSecurityPackageDataType[],
  queryParam?: { simple_package?: string; combined_package?: string },
):
  | { simple_package?: string; combined_package?: string }
  | { combined_package: number }
  | { simple_package: number }
  | {
      simple_package: number;
      security_items: { fundamental_id: number; security: string }[];
    }
  | {
      simple_package: number;
      pdf_items: { fundamental_id: number; security: string }[];
    } => {
  const pdfOrSecurityPackages = packageList?.map((item) => ({
    security: item.security,
    ...(item.fundamentalId && { fundamental_id: item.fundamentalId }),
  }));
  return queryParam
    ? { ...queryParam }
    : !selectedPackage?.is_simple
    ? { combined_package: selectedPackage.id }
    : !packageList
    ? { simple_package: selectedPackage.id }
    : selectedPackage.package_type === EnumPermission.securityPackage
    ? {
        simple_package: selectedPackage.id,
        security_items: pdfOrSecurityPackages,
      }
    : {
        simple_package: selectedPackage.id,
        pdf_items: pdfOrSecurityPackages,
      };
};

export const checkCouponAppliedOnPackage = (
  item: PackageType,
  basketData: BasketType,
): boolean =>
  basketData?.applied_coupon.selected_packages?.[
    item.is_simple ? 'simple_packages' : 'combined_packages'
  ].includes(item.id);

const getDuration = (duration: number): string =>
  duration > 29
    ? `${Math.round(duration / 30)} ${i18n.global.month}`
    : `${duration} ${i18n.global.day}`;

export const generateBasketList = (
  basket: BasketType,
): { title: string; value: string | boolean; id?: string }[] => {
  let list = [];
  const fa = i18n.wallet.basket;
  const isPending = basket?.status === 'Pending';
  const hasAppliedCoupon = !!basket?.applied_coupon?.coupon_code;
  const packages = basket?.items?.simple.concat(basket?.items.combined);
  const { total_amount, total_amount_with_taxes } =
    hasAppliedCoupon && basket.applied_coupon;
  const discount = hasAppliedCoupon
    ? addTooman(basket.applied_coupon.total_discount)
    : '-';

  const tax = hasAppliedCoupon
    ? total_amount_with_taxes - total_amount
    : basket?.total_fee_with_tax - basket?.total_fee; // eslint-disable-line

  const totalAmount = hasAppliedCoupon
    ? total_amount_with_taxes
    : basket?.total_fee_with_tax;

  if (!packages?.length) return null;

  packages.forEach((item) => {
    const discountPercentage = item?.auto_applied_coupon?.discount_percentage;
    const itemDiscount = checkCouponAppliedOnPackage(item, basket)
      ? addTooman((item.price * discountPercentage) / 100)
      : '-';

    const payAmount = checkCouponAppliedOnPackage(item, basket)
      ? (item.price * (100 - discountPercentage)) / 100
      : item.price;
    list = [
      ...list,
      { title: fa.productName, value: item.name },
      { title: fa.duration, value: getDuration(item.package_duration) },
      { title: fa.amount, value: addTooman(item.price) },
      { title: fa.discount, value: itemDiscount },
      { title: fa.payAmount, value: addTooman(payAmount) },
    ];

    !isPending &&
      list.push({ title: 'delete', value: item.is_simple, id: item.id });
  });

  list.push({ title: fa.total, value: addTooman(basket?.total_fee) });
  hasAppliedCoupon && list.push({ title: fa.discount, value: discount });
  list.push({ title: fa.tax, value: tax ? addTooman(tax) : '-' });
  list.push({ title: fa.totalAmount, value: addTooman(totalAmount) });

  return list;
};

const normalizeBasketItems = (basketData: BasketType): object[] => {
  const packages = basketData?.items.simple.concat(basketData?.items.combined);
  return packages?.map((item) => ({
    item_id: item.id,
    item_name: item.name,
    coupon: basketData?.applied_coupon?.coupon_code || '',
    discount: checkCouponAppliedOnPackage(item, basketData)
      ? // divided by 100 for percentage and divided by 1000 equal 1000000
        (item.price * basketData.applied_coupon.discount_percentage) / 100000
      : '',
    item_category: getDuration(item.package_duration),
    price: item.price / 1000,
    quantity: 1,
  }));
};

export const putCheckoutDataLayer = (basketData: BasketType): void => {
  const items = normalizeBasketItems(basketData);
  if (typeof window !== undefined && !window?.dataLayer) {
    window.dataLayer = window.dataLayer || [];
  }
  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  window.dataLayer.push({
    event: 'add_payment_info',
    ecommerce: {
      payment_type: 'online',
      items,
    },
  });
};

export const putPurchaseDataLayer = (
  transactionId: string | string[],
  basketData: BasketType,
): void => {
  // eslint-disable-next-line valid-typeof
  if (typeof window !== undefined && !window?.dataLayer) {
    window.dataLayer = window.dataLayer || [];
  }
  const items = normalizeBasketItems(basketData);
  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  window.dataLayer.push({
    event: 'purchase',
    ecommerce: {
      transaction_id: transactionId,
      value:
        (basketData?.applied_coupon?.total_amount_with_taxes ||
          basketData.total_fee_with_tax) / 1000,
      currency: 'USD',
      coupon: basketData?.applied_coupon?.coupon_code || '',
      items,
    },
  });
};
